import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  BookOpen,
  Globe,
  Search,
  Shield,
  Link2,
  Mail,
  Phone,
  CreditCard,
  Users,
  Download,
  BarChart3,
  Code,
  AlertTriangle,
  CheckCircle,
  ArrowLeft
} from "lucide-react";
import { useNavigate } from "react-router-dom";

const UserGuidePage = () => {
  const navigate = useNavigate();

  const sections = [
    {
      id: "getting-started",
      title: "Getting Started",
      icon: Globe,
      content: [
        {
          question: "What is NetID Tracker?",
          answer: "NetID Tracker is a comprehensive IP logging and identification dashboard that helps you monitor, analyze, and investigate network activity. It's perfect for website owners, security professionals, and anyone who needs to track visitor information."
        },
        {
          question: "How do I start tracking IPs?",
          answer: "There are three ways to track IPs:\n\n1. **Tracking Pixel**: Go to 'Tracking Pixel' in the sidebar to get a JavaScript snippet or image URL to embed on your website.\n\n2. **Link Tracker**: Create shortened links that log visitor IPs when clicked.\n\n3. **Direct API**: Use our API endpoints to log IPs programmatically."
        },
        {
          question: "What information is captured?",
          answer: "For each visitor, we capture:\n• IP Address\n• Geolocation (Country, City, Region)\n• ISP/Organization\n• Device type and browser\n• Operating system\n• VPN/Proxy detection status\n• Timestamp"
        }
      ]
    },
    {
      id: "investigation-tools",
      title: "Investigation Tools",
      icon: Search,
      content: [
        {
          question: "How does Email Header Analysis work?",
          answer: "Paste the full email headers from a suspicious email, and our analyzer will:\n• Extract all sender IPs along the delivery path\n• Check for SPF, DKIM, and DMARC authentication\n• Identify potential spoofing attempts\n• Show the geographic origin of the email\n\nTo get email headers: In Gmail, click the three dots → 'Show original'. In Outlook, go to File → Properties → Internet headers."
        },
        {
          question: "What does Phone Number Lookup reveal?",
          answer: "Our phone lookup (powered by Numverify) shows:\n• Carrier name (AT&T, Verizon, etc.)\n• Line type (Mobile, Landline, VoIP)\n• Country and location\n• Whether it's a VoIP number (commonly used by scammers)\n\nNote: VoIP numbers like Google Voice are often used for anonymous communications."
        },
        {
          question: "How accurate is VPN Detection?",
          answer: "Our VPN detection has two modes:\n\n• **Basic Mode (Free)**: ~30% accuracy using keyword matching\n• **Enhanced Mode (Paid tiers)**: 95%+ accuracy using IPQualityScore\n\nEnhanced detection identifies VPNs, proxies, Tor exit nodes, datacenter IPs, and bots."
        },
        {
          question: "What is Email Reputation Check?",
          answer: "Email reputation analysis (via EmailRep.io) reveals:\n• Whether the email has been seen in data breaches\n• Account age and first/last seen dates\n• If it's a disposable/temporary email\n• Suspicious activity history\n• Spam and malware associations"
        }
      ]
    },
    {
      id: "tracking-pixel",
      title: "Tracking Pixel & Links",
      icon: Code,
      content: [
        {
          question: "How do I use the Tracking Pixel?",
          answer: "Go to 'Tracking Pixel' in the sidebar. You'll get two options:\n\n1. **JavaScript Snippet**: Add this to your website's HTML for comprehensive tracking including device info.\n\n2. **Image Pixel**: A 1x1 transparent GIF that logs IPs when loaded. Works in emails and websites where JavaScript isn't allowed."
        },
        {
          question: "How does Link Tracking work?",
          answer: "1. Go to 'Link Tracker' in the sidebar\n2. Enter any destination URL\n3. We create a short link (e.g., yourdomain.com/r/abc123)\n4. Share this link\n5. When clicked, we log the visitor's IP and redirect them to your destination\n\nPerfect for tracking who clicks links in emails, social media, or documents."
        },
        {
          question: "Can I track email opens?",
          answer: "Yes! Use the image pixel in your emails:\n\n1. Get your pixel URL from 'Tracking Pixel'\n2. Insert it as an image in your email (many email clients support this)\n3. When the recipient opens the email, the image loads and logs their IP\n\nNote: This won't work if the recipient has images disabled."
        }
      ]
    },
    {
      id: "blacklist-alerts",
      title: "Blacklist & Alerts",
      icon: Shield,
      content: [
        {
          question: "How does the Blacklist work?",
          answer: "Add suspicious IPs to your blacklist to:\n• Flag future visits from these IPs as threats\n• Receive immediate alerts when they visit\n• Track repeated access attempts\n\nYou can also add IPs to a Whitelist to mark them as trusted and prevent false positives."
        },
        {
          question: "What triggers threat alerts?",
          answer: "Alerts are triggered when:\n• A blacklisted IP visits your tracked properties\n• VPN/proxy usage is detected (configurable)\n• Multiple requests from the same IP in a short time\n• Known malicious IP ranges are detected"
        },
        {
          question: "How do I manage alerts?",
          answer: "Go to 'Alerts' in the sidebar to:\n• View all active alerts\n• See detailed information about each threat\n• Mark alerts as resolved\n• Add notes for your records"
        }
      ]
    },
    {
      id: "subscription",
      title: "Subscription & Billing",
      icon: CreditCard,
      content: [
        {
          question: "What's included in each tier?",
          answer: "**Free**: 2 phone lookups, 2 email lookups, 2 IP lookups/month. Basic VPN detection.\n\n**Basic ($9.99/mo)**: 20 of each lookup type, 50 IP lookups, 95% VPN detection, CSV/JSON export.\n\n**Advanced ($24.99/mo)**: 100 of each lookup type, 500 IP lookups, API access.\n\n**Professional ($49.99/mo)**: 500 of each lookup type, 2,500 IP lookups, priority support."
        },
        {
          question: "How do I upgrade my plan?",
          answer: "1. Go to 'Subscription' in the sidebar\n2. Choose your desired plan\n3. Click 'Upgrade'\n4. Complete payment via Stripe\n5. Your account is upgraded immediately"
        },
        {
          question: "Where can I see my invoices?",
          answer: "Go to 'Invoices' in the sidebar to view:\n• All past payments\n• Current billing status\n• Next billing date\n• Download invoice records"
        }
      ]
    },
    {
      id: "export-api",
      title: "Export & API",
      icon: Download,
      content: [
        {
          question: "How do I export my data?",
          answer: "Go to 'IP Logs' and use the export buttons:\n• **CSV**: Spreadsheet-compatible format for Excel/Google Sheets\n• **JSON**: Machine-readable format for developers\n\nExport includes all logged data: IPs, locations, timestamps, device info, and threat assessments."
        },
        {
          question: "Is there an API available?",
          answer: "Yes! API access is available on Advanced and Professional tiers. You can:\n• Log IPs programmatically\n• Query your logs\n• Run investigation lookups\n• Manage blacklists/whitelists\n\nAPI documentation is available upon request."
        }
      ]
    }
  ];

  return (
    <div className="space-y-6" data-testid="user-guide-page">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="sm" onClick={() => navigate(-1)} className="gap-2">
          <ArrowLeft className="w-4 h-4" />
          Back
        </Button>
        <div>
          <h1 className="text-2xl md:text-3xl font-mono font-bold tracking-tight flex items-center gap-3">
            <BookOpen className="w-7 h-7 text-primary" />
            User Guide
          </h1>
          <p className="text-muted-foreground text-sm mt-1">
            Everything you need to know about NetID Tracker
          </p>
        </div>
      </div>

      {/* Quick Start */}
      <Card className="bg-primary/5 border-primary/20">
        <CardContent className="p-6">
          <div className="flex items-start gap-4">
            <CheckCircle className="w-6 h-6 text-primary mt-1 flex-shrink-0" />
            <div>
              <h3 className="font-semibold text-lg">Quick Start</h3>
              <p className="text-muted-foreground mt-1">
                To start tracking IPs immediately:
              </p>
              <ol className="list-decimal list-inside mt-2 space-y-1 text-sm">
                <li>Go to <strong>Tracking Pixel</strong> in the sidebar</li>
                <li>Copy the JavaScript snippet or image URL</li>
                <li>Add it to your website or email</li>
                <li>View tracked IPs in your <strong>Dashboard</strong></li>
              </ol>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Guide Sections */}
      <div className="grid gap-6">
        {sections.map((section) => (
          <Card key={section.id} className="bg-card/50 border-border">
            <CardHeader>
              <CardTitle className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                  <section.icon className="w-5 h-5 text-primary" />
                </div>
                {section.title}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <Accordion type="single" collapsible className="w-full">
                {section.content.map((item, index) => (
                  <AccordionItem key={index} value={`${section.id}-${index}`}>
                    <AccordionTrigger className="text-left hover:no-underline">
                      {item.question}
                    </AccordionTrigger>
                    <AccordionContent className="text-muted-foreground whitespace-pre-line">
                      {item.answer}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Contact Support */}
      <Card className="bg-card/50 border-border">
        <CardContent className="p-6">
          <div className="flex items-start gap-4">
            <AlertTriangle className="w-6 h-6 text-amber-500 mt-1 flex-shrink-0" />
            <div>
              <h3 className="font-semibold text-lg">Need More Help?</h3>
              <p className="text-muted-foreground mt-1">
                If you have questions not covered in this guide, or need assistance with your account:
              </p>
              <ul className="mt-2 space-y-1 text-sm">
                <li>• <strong>Professional tier</strong> users have access to priority support</li>
                <li>• Contact us at <span className="text-primary">support@netidtracker.com</span></li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default UserGuidePage;
